import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { parseYYYYMMDDToLocalDate } from "@/lib/dateOnly";

interface DatePickerFieldProps {
  /** YYYY-MM-DD string or null. */
  value: string | null | undefined;
  /** Emits YYYY-MM-DD or null. */
  onChange: (value: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  align?: "start" | "center" | "end";
  /** date-fns format string for the trigger label. Defaults to "PPP". */
  displayFormat?: string;
}


function toYMD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Universal date picker used across the app — shadcn Popover + Calendar.
 * Stores/exposes value as YYYY-MM-DD per project date policy (see src/lib/dateOnly.ts).
 */
export function DatePickerField({
  value, onChange, disabled, placeholder = "Pick a date", className, align = "start", displayFormat = "PPP",
}: DatePickerFieldProps) {
  const selected = value ? parseYYYYMMDDToLocalDate(value) : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selected ? format(selected, displayFormat) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => onChange(d ? toYMD(d) : null)}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}
